import { Box, Typography } from "@mui/material";
import {
  NotificationPreferenceScope,
  NotificationPreferenceSource,
} from "generated-graphql/graphql";

import { PREFERENCE_GRID_COLUMNS } from "./AlertRow";
import { PreferenceCheckbox } from "./PreferenceCheckbox";
import {
  CHANNEL_CELL_SX,
  channelLabel,
  MUTED_CELL_SX,
  mutedByChannelParentCopy,
  provenanceLabel,
  ROLLUP_DESCRIPTION,
  rollupLabel,
} from "./preferenceProvenance";
import {
  draftKey,
  isParentChannelOn,
  NO_PENDING_KEYS,
  PREFERENCE_ROW_CHANNELS,
  sectionChannelKeys,
  sectionChannelState,
  type DeliveryRow,
  type DeliverySection,
  type DraftParentSwitch,
  type NotificationPreferenceDraft,
} from "./useNotificationPreferenceDraft";

/**
 * A section's first row: one checkbox per channel that reports and sets every alert
 * in the section.
 *
 * It has a row of its own so it can carry a name and a description, which it needs
 * because it writes the alerts below it while the channel switches above only
 * silence them.
 */

export type SectionRollupRowProps = {
  /**
   * Assignable to {@link DeliverySection}, but spelled out: intersecting the two
   * would make `find` return the element type without `source`.
   */
  section: {
    label: string;
    parentSwitches: readonly (DraftParentSwitch & { source: NotificationPreferenceSource })[];
    rows: readonly DeliveryRow[];
  };
  channelParentSwitches: readonly DraftParentSwitch[];
  draft: NotificationPreferenceDraft;
  scope: NotificationPreferenceScope;
  /** True while a write is in flight; see {@link PreferenceCheckbox}. */
  disabled: boolean;
  /** Toggles whose own save is still in flight; each is disabled until it lands. */
  pendingKeys?: ReadonlySet<string>;
  setSectionChannel: (section: DeliverySection, channel: string, enabled: boolean) => void;
};

export function SectionRollupRow({
  section,
  channelParentSwitches,
  draft,
  scope,
  disabled,
  pendingKeys = NO_PENDING_KEYS,
  setSectionChannel,
}: SectionRollupRowProps) {
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: PREFERENCE_GRID_COLUMNS,
        alignItems: "center",
        columnGap: 1,
        py: 1,
        // Heavier than the rule between alerts, so the row reads as governing the
        // list rather than as the first entry in it.
        borderBottom: 2,
        borderColor: "divider",
      }}
    >
      <Box>
        <Typography variant="body1" fontWeight={500}>
          {rollupLabel(section.label)}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {ROLLUP_DESCRIPTION}
        </Typography>
      </Box>

      {PREFERENCE_ROW_CHANNELS.map((channel) => {
        const supported = section.rows.some((row) =>
          row.preferences.some((preference) => preference.channel === channel)
        );
        // No alert here uses this channel, so the column below is all N/A.
        if (!supported) {
          return <Box key={channel} sx={CHANNEL_CELL_SX} />;
        }

        const state = sectionChannelState(section, draft, channel);
        // The page-wide switch for this channel. Off, it greys the cell the same way
        // it greys the alerts below.
        const channelMuted = !isParentChannelOn(draft, channelParentSwitches, channel);
        const parent = section.parentSwitches.find((entry) => entry.channel === channel);

        return (
          <Box key={channel} sx={{ ...CHANNEL_CELL_SX, ...(channelMuted ? MUTED_CELL_SX : {}) }}>
            <PreferenceCheckbox
              label={`${channelLabel(channel)} for all ${section.label} notifications`}
              explanation={
                state !== "off" && channelMuted
                  ? mutedByChannelParentCopy(channel)
                  : provenanceLabel(parent?.source ?? NotificationPreferenceSource.Default, scope)
              }
              // Checked only at `on`, so a click from mixed fills the category in
              // rather than clearing it.
              checked={state === "on"}
              indeterminate={state === "mixed"}
              disabled={
                disabled ||
                sectionChannelKeys(section, channel).some((toggle) =>
                  pendingKeys.has(draftKey(toggle.kind, toggle.channel))
                )
              }
              onChange={(enabled) => setSectionChannel(section, channel, enabled)}
            />
          </Box>
        );
      })}

      {/* The header's caret lane, reserved so the columns line up. */}
      <Box />
    </Box>
  );
}
